// Решётка «текст → слово → фраза × блок». Чистые функции: DOM и состояние живут в HeadGrid.vue.
// Сессия — объект К2; reached = индекс самого глубокого блока или -1.
// Сессии с reached = -1 (ни одного блока) в решётку не входят вовсе: иначе они сидели бы
// в знаменателе воронки и тянули медиану к нулю, а первый блок переставал быть 100 %.

const NO_DATA = 'нет данных';     // показ не записан (старше 90 дней или до лога)
const NO_SWAP = 'без подмены';    // показ известен, но заголовка нет
const TAIL = [NO_SWAP, NO_DATA];  // служебные тексты всегда в хвосте

// Нижняя медиана: как в основном файле, иначе числа решётки разошлись бы со сценой.
const median = a => { const z = [...a].sort((x, y) => x - y); return z.length ? z[(z.length - 1) >> 1] : 0; };
const mean = a => a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0;

// Воронка: доля сессий, ДОШЕДШИХ до блока k. Растёт вправо она не может, рост означал бы ошибку счёта.
export function funnel(reachedList, nBlocks) {
  const h = Array(nBlocks).fill(0);
  reachedList.forEach(r => { for (let k = 0; k < nBlocks && k <= r; k++) h[k]++; });
  const n = reachedList.length || 1;
  return h.map(v => v / n);
}

// Средний отток по шагам: доля уходящих от тех, кто до шага дошёл. Разность соседних долей
// телескопируется и о форме воронки не говорит, а доля от оставшихся её ловит.
// Шаги после нуля не считаем: терять уже некого.
export function avgChurn(h) {
  let sum = 0, steps = 0;
  for (let k = 0; k + 1 < h.length; k++) {
    if (h[k] <= 0) break;
    sum += (h[k] - h[k + 1]) / h[k];
    steps++;
  }
  return steps ? sum / steps : 0;
}

function stats(reach, nBlocks) {
  const r = [...reach.values()], blk = r.map(x => x + 1);
  return { n: r.length, hist: funnel(r, nBlocks), med: median(blk), m: mean(blk) };
}

// convTypesCount не нужен: конверсия сессии = convMask != 0 при любом числе типов.
// Параметр оставлен ради контракта К-плана.
export function buildTree(sessions, phraseWords, nBlocks, convTypesCount) { // eslint-disable-line no-unused-vars
  const roots = new Map();
  const node = (map, key, level, label, sub) => {
    if (!map.has(key)) map.set(key, { key, level, label, sub, reach: new Map(), convIds: new Set(), kids: new Map() });
    return map.get(key);
  };
  const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
  sessions.forEach(s => {
    if (!(s.reached >= 0)) return;
    const special = s.title ? null : (s.shownSource == null ? NO_DATA : NO_SWAP);
    const tKey = special || s.title + ' ¦ ' + (s.subtitle || '');
    const t = node(roots, tKey, 0, special || s.title, special ? '' : (s.subtitle || ''));
    // Фраза без слов в словаре сама становится «словом», чтобы сессия не пропадала из дерева.
    const words = has(phraseWords, s.phrase) && phraseWords[s.phrase].length ? phraseWords[s.phrase] : [s.phrase];
    const chain = [t];
    words.forEach(w => {
      const wn = node(t.kids, tKey + '/w:' + w, 1, w, '');
      const pn = node(wn.kids, wn.key + '/p:' + s.phrase, 2, s.phrase, '');
      [wn, pn].forEach(n => chain.push(n));
    });
    chain.forEach(n => {
      n.reach.set(s.id, s.reached);          // Map по id: сессия под двумя словами считается один раз
      if (s.convMask) n.convIds.add(s.id);
    });
  });
  const finish = n => ({
    key: n.key, level: n.level, label: n.label, sub: n.sub, ...stats(n.reach, nBlocks),
    conv: n.convIds.size, ids: new Set(n.reach.keys()),
    // Листья несут по-сессионные глубину и конверсию: из них считаются сборки строк.
    ...(n.level === 2 ? { reach: n.reach, convIds: n.convIds } : {}),
    children: [...n.kids.values()].map(finish),
  });
  const tail = r => TAIL.indexOf(r.key);   // -1 у обычных текстов
  return [...roots.values()].map(finish).sort((a, b) => (tail(a) >= 0) - (tail(b) >= 0) || tail(a) - tail(b) || a.med - b.med || a.m - b.m);
}

const fix2 = v => Math.round(v * 100) / 100;
export const METRICS = {
  n:     { name: 'сессии',         val: r => r.n,    print: r => r.n },
  conv:  { name: 'конверсии',      val: r => r.conv, print: r => r.conv || '—' },
  med:   { name: 'медианный блок', val: r => r.med,  print: r => r.med },
  m:     { name: 'средний блок',   val: r => r.m,    print: r => fix2(r.m).toFixed(2).replace('.', ',') },
  churn: { name: 'средний отток, %', val: r => avgChurn(r.hist) * 100, print: r => Math.round(avgChurn(r.hist) * 100) },
};

// Один уровень; рекурсия по детям остаётся за вызывающим (у каждого уровня своё правило).
// rule = {field: 'name' | ключ METRICS | 'b:<k>', dir: 1 | -1} или null (порядок не трогаем).
// Служебные тексты («без подмены», «нет данных») стоят в хвосте при любом правиле; у текстов сборки
// ключ с префиксом «asm:id/», поэтому сверяем и хвост ключа.
const tailOf = r => TAIL.findIndex(k => r.key === k || (r.key || '').endsWith('/' + k));
export function sortLevel(list, rule) {
  if (!rule || !rule.field) return list;
  const { field, dir } = rule;
  const f = field === 'name' ? null : field.startsWith('b:') ? (r => r.hist[+field.slice(2)]) : METRICS[field] && METRICS[field].val;
  if (field !== 'name' && !f) return list;
  const byName = (a, b) => a.label.localeCompare(b.label, 'ru');
  // Имя вторым ключом: при равных числах порядок не прыгает от перерисовки.
  return list.slice().sort((a, b) => (tailOf(a) >= 0) - (tailOf(b) >= 0) || tailOf(a) - tailOf(b)
    || (f ? (f(a) - f(b)) * dir || byName(a, b) : byName(a, b) * dir));
}

// Родитель каждой строки плоского списка: ближайшая выше строка меньшего уровня.
// У верхнего уровня родителя нет (null): вызывающий сравнивает его со всем срезом.
export function parentBase(rows) {
  const stack = [];
  return rows.map(r => {
    const lv = r.level || 0;
    stack.length = lv;                          // всё глубже предком уже не является
    const p = lv === 0 ? null : (stack[lv - 1] || null);
    stack[lv] = r;
    return p;
  });
}

// Медиана, максимум и сумма каждой метрики по уровням, по всем строкам дерева, а не только раскрытым.
export function levelMedians(roots, metricKeys) {
  const acc = {};
  const walk = list => list.forEach(r => {
    const lv = r.level || 0;
    (acc[lv] = acc[lv] || []).push(r);
    if (r.children && r.children.length) walk(r.children);
  });
  walk(roots);
  const out = {};
  Object.keys(acc).forEach(lv => {
    out[lv] = {};
    metricKeys.forEach(k => {
      const z = acc[lv].map(METRICS[k].val);
      out[lv][k] = { med: median(z), max: z.reduce((m, v) => Math.max(m, v), 0), sum: z.reduce((a, b) => a + b, 0), n: z.length };
    });
  });
  return out;
}

// Сборка строк: атом {text, phrase}, text = ключ узла текста; phrase null/undefined = весь текст.
// Вход считается один раз (дедуп по id), а «повтор» это сессия, попавшая в сборку через РАЗНЫЕ атомы
// (фраза под двумя словами одного атома повтором не считается). raw = сумма размеров атомов до
// дедупа («сумма частей» прототипа). Узлы byText/children имеют те же поля, что узлы buildTree.
export function assemblyCount(parts, tree, nBlocks) {
  const nb = nBlocks || (tree[0] ? tree[0].hist.length : 0);
  const seen = new Map();          // id -> {reach, conv, text, phrase}
  const times = new Map();         // id -> в скольких атомах встретилась
  let raw = 0;
  const atoms = new Set();
  parts.forEach(p => {
    const ak = JSON.stringify([p.text, p.phrase == null ? null : p.phrase]);
    if (atoms.has(ak)) return;     // один и тот же атом дважды не считаем
    atoms.add(ak);
    const t = tree.find(r => r.key === p.text);
    if (!t) return;
    const own = new Map();
    t.children.forEach(w => w.children.forEach(ph => {
      if (p.phrase != null && ph.label !== p.phrase) return;
      ph.reach.forEach((r, id) => own.set(id, { reach: r, conv: ph.convIds.has(id), text: t, phrase: ph.label }));
    }));
    raw += own.size;
    own.forEach((v, id) => {
      times.set(id, (times.get(id) || 0) + 1);
      if (!seen.has(id) || v.reach > seen.get(id).reach) seen.set(id, v);
    });
  });
  const mk = (key, level, label, sub, items, children) => {
    const reach = new Map(items.map(([id, v]) => [id, v.reach]));
    return { key, level, label, sub, ...stats(reach, nb), conv: items.filter(([, v]) => v.conv).length, ids: new Set(reach.keys()), children };
  };
  const byT = new Map();
  seen.forEach((v, id) => { if (!byT.has(v.text.key)) byT.set(v.text.key, { t: v.text, list: [] }); byT.get(v.text.key).list.push([id, v]); });
  const byText = [...byT.values()].map(({ t, list }) => {
    const byP = new Map();
    list.forEach(e => { if (!byP.has(e[1].phrase)) byP.set(e[1].phrase, []); byP.get(e[1].phrase).push(e); });
    return mk(t.key, 0, t.label, t.sub, list, [...byP.entries()].map(([ph, l]) => mk(t.key + '/p:' + ph, 1, ph, '', l, [])));
  });
  const all = mk('', 0, '', '', [...seen.entries()], []);
  return { n: all.n, hist: all.hist, med: all.med, m: all.m, conv: all.conv, ids: all.ids, raw, dups: [...times.values()].filter(v => v > 1).length, byText };
}
